import { useMemo } from 'react';
// Artifact type constants (from proto)
const ARTIFACT_TYPE_VECTOR = 2;
const formatContourLabel = (name) => {
    // Match patterns like "contours_0.5m_display.kml" or "contour_2ft.geojson"
    const contourMatch = name.match(/^contours?_([0-9.]+)(ft|m)(?:_display)?\.(?:kml|dxf|geojson|shp\.zip)$/i);
    if (contourMatch) {
        const value = contourMatch[1];
        const unit = contourMatch[2].toLowerCase();
        return `${value}${unit}`;
    }
    // Match patterns like "0.5m" or "2ft"
    const unitMatch = name.match(/^(\d+(?:\.\d+)?)\s*(m|ft)$/i);
    if (unitMatch) {
        return `${unitMatch[1]}${unitMatch[2].toLowerCase()}`;
    }
    // For names containing "contour", try to extract just the measurement
    const contourNameMatch = name.match(/(\d+(?:\.\d+)?)\s*(m|ft|meter|feet)/i);
    if (contourNameMatch) {
        const unit = contourNameMatch[2].toLowerCase().startsWith('f') ? 'ft' : 'm';
        return `${contourNameMatch[1]}${unit}`;
    }
    return name;
};
const STREAM_ORDER = { micro: 0, minor: 1, intermediate: 2, intermed: 2, major: 3 };
const formatStreamLabel = (name) => {
    const lower = name.toLowerCase();
    if (lower === 'micro')
        return 'Micro';
    if (lower === 'minor')
        return 'Minor';
    if (lower === 'intermediate')
        return 'Intermed';
    if (lower === 'major')
        return 'Major';
    return name;
};
export const isContourName = (name) => {
    const lowerName = name.toLowerCase();
    return lowerName.includes('contour') ||
        lowerName === '0.5m' || lowerName === '1m' || lowerName === '2m' || lowerName === '3m' ||
        /^\d+(\.\d+)?m$/.test(lowerName) || /^\d+(\.\d+)?ft$/.test(lowerName);
};
// --- Low-resolution scouting contour gating --------------------------------
// On the low-res scouting DEM (~15m of real information) only the coarsest
// contour intervals are meaningful — finer lines trace sampling noise, not
// real ground. These per-unit floors are the smallest intervals that survive;
// anything below is gated behind a full-resolution unlock (ContourResolutionModal).
// User-facing rule: 8ft+ (imperial) and 2m+ (metric) work in low-res. These
// mirror the backend's scoutingMinContourIntervalMeters (2.0m): everything the
// gate ALLOWS is actually generated for the scouting set, so nothing 404s.
export const LOW_RES_MIN_CONTOUR_FT = 8;
export const LOW_RES_MIN_CONTOUR_M = 2;
// parseContourInterval pulls the numeric interval + unit out of a contour
// artifact name (e.g. "contours_4ft_display.kml" -> {value:4, unit:'ft'},
// "2m" -> {value:2, unit:'m'}). Returns null for non-contour / unparseable
// names so callers fail open.
export const parseContourInterval = (name) => {
    // Matches every shape the interval shows up in: "contours_2ft",
    // "contours_2ft_display", "contours_0.5m.geojson", "contours_12ft.kml".
    // The unit is followed by "_" (e.g. _display), "." (extension), or the end of
    // the string — NOT a \b assertion, since "_" is a word char and would defeat
    // the boundary right after the unit in "..._2ft_display".
    const prefixed = name.match(/contours?_([0-9]*\.?[0-9]+)(ft|m)(?:[_.]|$)/i);
    // Bare interval labels like "2ft" / "0.5 m".
    const bare = prefixed ? null : name.match(/^([0-9]*\.?[0-9]+)\s*(ft|m)$/i);
    const match = prefixed !== null && prefixed !== void 0 ? prefixed : bare;
    if (!match)
        return null;
    const value = parseFloat(match[1]);
    if (!isFinite(value) || value <= 0)
        return null;
    return { value, unit: match[2].toLowerCase() === 'ft' ? 'ft' : 'm' };
};
// isContourBlockedAtLowRes is true when a contour interval is too fine to be
// meaningful on the low-res scouting DEM and should be gated behind an unlock.
export const isContourBlockedAtLowRes = (name) => {
    const parsed = parseContourInterval(name);
    if (!parsed)
        return false;
    return parsed.unit === 'ft'
        ? parsed.value < LOW_RES_MIN_CONTOUR_FT
        : parsed.value < LOW_RES_MIN_CONTOUR_M;
};
// formatContourInterval renders a parsed interval for display, e.g. "4 ft".
export const formatContourInterval = (name) => {
    const parsed = parseContourInterval(name);
    return parsed ? `${parsed.value} ${parsed.unit}` : name;
};
export const isStreamName = (name, metadataJson) => {
    const lowerName = name.toLowerCase();
    // Check metadata for stream/drainage keywords
    if (metadataJson) {
        try {
            const meta = JSON.parse(metadataJson);
            const objectKey = (meta.object_key || '').toLowerCase();
            if (objectKey.includes('stream') || objectKey.includes('drainage')) {
                return true;
            }
        }
        catch (_a) {
            // JSON parse failed, continue
        }
    }
    // Check name for stream keywords
    return lowerName.includes('stream') || lowerName.includes('hydro') || lowerName.includes('drainage') ||
        ['micro', 'minor', 'intermediate', 'major'].includes(lowerName);
};
export const useLayerTypeDetection = (loadedArtifacts, isArtifactVisible, projectArtifacts) => {
    return useMemo(() => {
        var _a, _b;
        const contourLayers = [];
        const streamLayers = [];
        const seenIds = new Set();
        // First, check project artifacts (all available layers)
        if (projectArtifacts && projectArtifacts.length > 0) {
            for (const artifact of projectArtifacts) {
                const artifactId = artifact.getId();
                const artifactType = artifact.getType();
                // Only look at vector artifacts
                if (artifactType !== ARTIFACT_TYPE_VECTOR)
                    continue;
                const name = artifact.getName();
                const metadataJson = artifact.getMetadata();
                const visible = isArtifactVisible(artifactId);
                seenIds.add(artifactId);
                if (isContourName(name)) {
                    contourLayers.push({ id: artifactId, label: formatContourLabel(name), visible });
                }
                else if (isStreamName(name, metadataJson)) {
                    streamLayers.push({ id: artifactId, label: formatStreamLabel(name), visible });
                }
            }
        }
        // Fallback: also check loaded artifacts (for any that might not be in project structure)
        for (const [artifactId, artifact] of loadedArtifacts.entries()) {
            if (seenIds.has(artifactId))
                continue;
            if (artifact.type !== 'vector')
                continue;
            const name = typeof ((_a = artifact.metadata) === null || _a === void 0 ? void 0 : _a.getName) === 'function'
                ? artifact.metadata.getName()
                : '';
            const metadataJson = typeof ((_b = artifact.metadata) === null || _b === void 0 ? void 0 : _b.getMetadata) === 'function'
                ? artifact.metadata.getMetadata()
                : '';
            const visible = isArtifactVisible(artifactId);
            if (isContourName(name)) {
                contourLayers.push({ id: artifactId, label: formatContourLabel(name), visible });
            }
            else if (isStreamName(name, metadataJson)) {
                streamLayers.push({ id: artifactId, label: formatStreamLabel(name), visible });
            }
        }
        // Sort contour layers numerically ascending (e.g., 0.5m, 1m, 2m, 3m)
        contourLayers.sort((a, b) => {
            const numA = parseFloat(a.label) || 0;
            const numB = parseFloat(b.label) || 0;
            return numA - numB;
        });
        // Sort stream layers by conventional ordering
        streamLayers.sort((a, b) => {
            var _a, _b;
            const orderA = (_a = STREAM_ORDER[a.label.split(' ')[0].toLowerCase()]) !== null && _a !== void 0 ? _a : 99;
            const orderB = (_b = STREAM_ORDER[b.label.split(' ')[0].toLowerCase()]) !== null && _b !== void 0 ? _b : 99;
            return orderA - orderB;
        });
        return {
            hasContourLayers: contourLayers.length > 0,
            hasStreamLayers: streamLayers.length > 0,
            contourLayers,
            streamLayers
        };
    }, [loadedArtifacts, isArtifactVisible, projectArtifacts]);
};
